/// <amd-module name="VSSUI/Components/PivotBar/PivotBarAction" />

import * as React from "react";

import { Button } from "VSSUI/Button";
import { IIconProps, Icon } from "VSSUI/Icon";
import { IContextualMenuItem, ContextualMenu, ContextualMenuItemType, DirectionalHint } from "OfficeFabric/ContextualMenu";
import { FocusZone, FocusZoneDirection, IFocusZone } from "OfficeFabric/FocusZone";
import { IconType } from "OfficeFabric/Icon";
import { Slider, ISliderProps } from "OfficeFabric/Slider";
import { Toggle } from "OfficeFabric/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { TooltipHost } from "VSSUI/Tooltip";
import { BaseComponent, findIndex, IBaseProps } from "OfficeFabric/Utilities";
import { css, KeyCode } from "VSSUI/Util";

import {
    IChoiceGroupViewActionOption,
    IChoiceGroupViewActionProps,
    IOnOffViewActionProps,
    IPivotBarAction,
    IPivotBarViewAction,
    ISliderViewActionProps,
    ITextViewActionProps,
    PivotBarViewActionType,
    ICommandViewActionProps
} from "../../Components/PivotBar/PivotBarAction.Props";
import { DynamicText } from "VSSUI/Dynamic";
import { IUserAction } from "VSSUI/Utilities/IUserAction";
import { IViewOptions } from "VSSUI/Utilities/ViewOptions";
import { IVssIconProps, VssIcon, VssIconType } from "VSSUI/VssIcon";
import * as VSSUI_Resources from "VSSUI/VSS.Resources.VSSUI";

const commandBarItemClassName = "vss-PivotBar--commandBar-item";

/**
 * Internal enum determining where to render the visualization representing the action.
 */
enum PivotBarActionArea {
    /**
     * Area where all the actions are visible by default.
     */
    Bar,

    /**
     * Area where the actions are hidden by default and exist inside an overflow menu.
     */
    Overflow
}

interface IPivotBarActionVisualization<TAction extends IPivotBarAction> {
    /**
     * Gets the command bar item(s) corresponding to the visualization of the action.
     */
    getVisualization(action: TAction, viewOptions: IViewOptions, forceUpdate?: () => void): IContextualMenuItem[];
}

interface PivotBarActionVisualizationType {
    new (): IPivotBarActionVisualization<IPivotBarAction>;
}

/**
 * Converts the specified actions to visual elements (IContextualMenuItem on the CommandBar).
 * @param actions List if actions to convert.
 * @param viewOptions View options to operate on.
 * @param forceUpdate Callback to force a rerender of the visualizations
 */
/** @deprecated */
export function mapActionsToItems(actions: IPivotBarAction[], viewOptions?: IViewOptions, forceUpdate?: () => void): IContextualMenuItem[] {
    return mapActionsToVisualizations(actions, PivotBarActionArea.Bar, viewOptions, forceUpdate);
}

/**
 * Converts an IContextualMenuItem to an IPivotBarAction
 * @param menuItem
 */
/** @deprecated */
export function mapItemToActions(menuItem: IContextualMenuItem): IPivotBarAction {
    const menuItemIconType = menuItem.iconProps.iconType;
    const pivotBarActionIconType =
        menuItemIconType === IconType.default || menuItemIconType === IconType.Default ? VssIconType.fabric : VssIconType.image;
    const action: IPivotBarAction = {
        key: menuItem.key,
        name: menuItem.name,
        href: menuItem.href,
        title: menuItem.title,
        iconProps: { ...menuItem.iconProps, iconType: pivotBarActionIconType } as IVssIconProps,
        onClick: menuItem.onClick,
        disabled: menuItem.disabled,
        important: menuItem.important,
        target: menuItem.target,
        separator: menuItem.itemType === ContextualMenuItemType.Divider
    };

    if (menuItem.items) {
        action.children = menuItem.items.map(mapItemToActions);
    }

    return action;
}

/**
 * Internal mapping from action to action representation.
 * @param actions List if actions to convert.
 * @param area Container action of the action.
 * @param viewOptions View options to operate on.
 * @param forceUpdate Callback to force a rerender of the visualizations
 */
function mapActionsToVisualizations(
    actions: IPivotBarAction[],
    area: PivotBarActionArea,
    viewOptions: IViewOptions,
    forceUpdate?: () => void
): IContextualMenuItem[] {
    let items: IContextualMenuItem[] = [];
    for (let action of actions || []) {
        const actionVisualization = VisualizationRegistration.getInstance(
            area,
            (action as IPivotBarViewAction).actionType || PivotBarViewActionType.Command
        );
        items = items.concat(actionVisualization.getVisualization(action, viewOptions, forceUpdate));
    }

    // Mark all CommandBar items with our class name
    items.forEach(item => {
        item.className = css(item.className, commandBarItemClassName);
    });

    return items;
}

function setViewOption(
    viewOptions: IViewOptions,
    key: string,
    value: any,
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    action: IUserAction
) {
    if (viewOptions) {
        viewOptions.setViewOption(key, value);
    } else if (typeof action.onClick === "function") {
        action.onClick(event, action);
    }
}

namespace VisualizationRegistration {
    const actionVisualizations: { [key: string]: PivotBarActionVisualizationType } = {};

    function getKey(area: PivotBarActionArea, actionType: PivotBarViewActionType): string {
        return `${area}:${actionType}`;
    }

    /** @deprecated */
    export function register(area: PivotBarActionArea, actionType: PivotBarViewActionType, visualizationType: PivotBarActionVisualizationType) {
        const key = getKey(area, actionType);
        actionVisualizations[key] = visualizationType;
    }

    /** @deprecated */
    export function getInstance(area: PivotBarActionArea, actionType: PivotBarViewActionType): IPivotBarActionVisualization<IPivotBarAction> {
        const key = getKey(area, actionType);
        let visualizationType = actionVisualizations[key];

        if (!visualizationType) {
            throw new Error(`No visualization found for area ${area} and action type ${actionType}`);
        }

        // Return the instance of the visualization
        return new visualizationType();
    }
}

/**
 * Command visualization which is same for both bar and overflow areas.
 */
class CommandVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const item: IContextualMenuItem = {
            key: action.key,
            name: action.name || "",
            ariaLabel: action.ariaLabel,
            className: action.actionProps && action.actionProps.className,
            title: action.title,
            onClick: action.onClick,
            href: action.href,
            disabled: action.disabled,
            target: action.target,
            itemType: action.separator ? ContextualMenuItemType.Divider : ContextualMenuItemType.Normal
        };

        if (action.iconProps) {
            const iconProps = { ...action.iconProps };
            iconProps.className = css(iconProps.className, "vss-PivotBar--commandBar-icon");
            item.iconProps = VssIcon.getIconProps(iconProps);
        }

        const actionProps = action.actionProps as ICommandViewActionProps;
        if (actionProps && actionProps.showChevron) {
            // We don't have a good way to owner draw a custom icon in the command bar, but this will
            // allow us to make the CommandBar *think* that there is a sub menu, so it will render the
            // chevron.
            item.subMenuProps = { items: [] };
        }

        // Default view action may have child actions
        if (Array.isArray(action.children)) {
            item.subMenuProps = {
                className: "vss-PivotBar--commandBar-overflow",
                items: mapActionsToVisualizations(action.children, PivotBarActionArea.Overflow, viewOptions)
            };
        }

        return [item];
    }
}

VisualizationRegistration.register(PivotBarActionArea.Bar, PivotBarViewActionType.Command, CommandVisualization);
VisualizationRegistration.register(PivotBarActionArea.Overflow, PivotBarViewActionType.Command, CommandVisualization);

/**
 * Base visualization for OnOff view action.
 */
abstract class OnOffVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        return [];
    }

    protected getActionProps(action: IPivotBarViewAction): IOnOffViewActionProps {
        if (!action.actionProps) {
            action.actionProps = { on: false } as IOnOffViewActionProps;
        }

        return action.actionProps as IOnOffViewActionProps;
    }

    protected isOn(key: string, viewOptions: IViewOptions): boolean {
        return viewOptions ? viewOptions.getViewOption(key) === true : false;
    }
}

/**
 * OnOff visualization for bar area.
 */
class OnOffBarVisualization extends OnOffVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions, forceUpdate?: () => void): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);
        const isOn = this.isOn(action.key, viewOptions);

        const itemNameValue = isOn ? VSSUI_Resources.PivotBarViewActionOnText : VSSUI_Resources.PivotBarViewActionOffText;

        // Show item name if not explicitly made invisible
        let itemName: string;
        if (actionProps.showText !== false) {
            itemName = itemNameValue;
        }

        // Try to get aria-label from the props, if not set, get it from item name if exists
        let itemAriaLabel = isOn ? actionProps.onAriaLabel : actionProps.offAriaLabel;
        let itemTitle = action.title;

        // Set aria-label to title/name if not excplicitly specified
        if (itemAriaLabel === undefined) {
            itemAriaLabel = itemTitle !== undefined ? itemTitle : itemNameValue;
        }

        // Set title to aria-label/name if not explicitly specified
        if (itemTitle === undefined) {
            itemTitle = itemAriaLabel !== undefined ? itemAriaLabel : itemNameValue;
        }

        // Try to get the icon from the action props first
        let iconProps = isOn ? actionProps.onIconProps : actionProps.offIconProps;

        // It might be specified to the view action directly
        if (!iconProps) {
            iconProps = action.iconProps;
        }

        const item = {
            key: action.key,
            onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void, posInSet?: number, setSize?: number) => {
                const button = (
                    <Button
                        subtle={true}
                        className={css("vss-PivotBar--commandBar-toggle", "pivotBar-toggle-button", actionProps.className)}
                        text={itemName}
                        ariaSetSize={setSize}
                        ariaPosInSet={posInSet}
                        ariaChecked={isOn}
                        disabled={action.disabled}
                        iconProps={VssIcon.getIconProps(iconProps, "vss-PivotBar--commandBar-icon") as IIconProps}
                        onClick={(ev: React.MouseEvent<any>) => {
                            setViewOption(viewOptions, item.key, !this.isOn(item.key, viewOptions), ev, action);
                            forceUpdate && forceUpdate();
                        }}
                        ariaLabel={itemAriaLabel}
                        role="switch"
                        tooltipProps={itemTitle ? { showOnFocus: true, text: itemTitle } : undefined}
                    />
                );

                if (itemTitle) {
                    return (
                        <Tooltip text={itemTitle}>
                            <span className="vss-PivotBar-commandBar-toggle-host">{button}</span>
                        </Tooltip>
                    );
                } else {
                    return button;
                }
            }
        } as IContextualMenuItem;

        return [item];
    }
}

VisualizationRegistration.register(PivotBarActionArea.Bar, PivotBarViewActionType.OnOff, OnOffBarVisualization);

/**
 * OnOff visualization for overflow area.
 */
class OnOffOverflowVisualization extends OnOffVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);
        let items: IContextualMenuItem[] = [];
        const onText = actionProps.onText || VSSUI_Resources.PivotBarViewActionOnText;
        const offText = actionProps.offText || VSSUI_Resources.PivotBarViewActionOffText;
        const onAriaLabel = actionProps.onAriaLabel || (action.ariaLabel ? action.ariaLabel : null);
        const offAriaLabel = actionProps.offAriaLabel || (action.ariaLabel ? action.ariaLabel : null);

        // Add an item with a custom rendering which uses a Toggle component
        const toggleItem = {
            key: action.key,
            onRender: (item: any) => {
                const toggle = (
                    <Toggle
                        key={`${action.key}-toggle`}
                        className={css("vss-PivotBar--commandBar-toggle", actionProps.className)}
                        styles={{ root: { paddingLeft: 8, marginTop: 4 }, container: { alignItems: "center" }, pill: { fontSize: "15px" } }}
                        checked={this.isOn(action.key, viewOptions)}
                        disabled={action.disabled}
                        onText={onText}
                        offText={offText}
                        onAriaLabel={onAriaLabel}
                        offAriaLabel={offAriaLabel}
                        onChanged={(value: boolean) => {
                            setViewOption(viewOptions, item.key, value, null, action);
                        }}
                    />
                );

                if (action.title) {
                    return (
                        <TooltipHost content={action.title} key={`${action.key}-toggle-tooltip`}>
                            {toggle}
                        </TooltipHost>
                    );
                }
                return toggle;
            }
        } as IContextualMenuItem;

        // Add section with header for the toggle inside a popup
        if (action.name) {
            items.push({
                key: action.key,
                sectionProps: {
                    items: [toggleItem],
                    title: action.name,
                    key: `${action.key}-section`
                },
                itemType: ContextualMenuItemType.Section
            });
        } else {
            items.push(toggleItem);
        }

        return items;
    }
}

VisualizationRegistration.register(PivotBarActionArea.Overflow, PivotBarViewActionType.OnOff, OnOffOverflowVisualization);

/**
 * Base visualization for ChoiceGroup view action.
 */
abstract class ChoiceGroupVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        return [];
    }

    protected getActionProps(action: IPivotBarViewAction): IChoiceGroupViewActionProps {
        if (!action.actionProps) {
            throw new Error(`Options must be specified for the choice group ${action.key}`);
        }

        return action.actionProps as IChoiceGroupViewActionProps;
    }

    protected getSelectedOption(key: string, actionProps: IChoiceGroupViewActionProps, viewOptions: IViewOptions): IChoiceGroupViewActionOption {
        const selectedOptionKey = (viewOptions ? viewOptions.getViewOption(key) : null) || actionProps.defaultSelectedKey;
        return actionProps.options.filter(o => o.key === selectedOptionKey)[0] || actionProps.options[0];
    }
}

interface IChoiceGroupButtonProps extends IBaseProps {
    ariaLabel?: string;
    dropButtonAriaLabel?: string;
    options: IChoiceGroupViewActionOption[];
    defaultSelectedKey?: string;
    dropButtonClassName?: string;
    onChange?: (ev: React.MouseEvent<HTMLElement>, option?: IChoiceGroupViewActionOption) => void;
}

interface IChoiceGroupButtonState {
    showDropdown?: boolean;
    selectedOptionIndex?: number;
    dropTarget?: HTMLButtonElement;
}

/**
 * A customized split button variation, which enables to select an option from a list of options when drop section is clicked.
 * Clicking button section will cycle through all available options.
 */
class ChoiceGroupButton extends BaseComponent<IChoiceGroupButtonProps, IChoiceGroupButtonState> {
    constructor(props: IChoiceGroupButtonProps) {
        super(props);
        this.state = {
            showDropdown: false,
            selectedOptionIndex: this._getSelectedOptionIndex(this.props.options, this.props.defaultSelectedKey)
        };
    }

    public componentWillReceiveProps(nextProps: IChoiceGroupButtonProps) {
        this.setState({
            showDropdown: false,
            selectedOptionIndex: this._getSelectedOptionIndex(nextProps.options, nextProps.defaultSelectedKey)
        });
    }

    public render(): JSX.Element {
        const { dropButtonClassName, ariaLabel, dropButtonAriaLabel, options } = this.props;
        const { selectedOptionIndex, showDropdown, dropTarget } = this.state;

        const selectedOption = options[selectedOptionIndex];

        return (
            <div className="vss-ChoiceGroupButton">
                <Button
                    subtle={true}
                    ariaLabel={ariaLabel || dropButtonAriaLabel || VSSUI_Resources.PivotBarMoreOptionsAriaLabel}
                    aria-expanded={showDropdown}
                    aria-haspopup={true}
                    className={css("vss-ChoiceGroupButton--drop", dropButtonClassName)}
                    text={selectedOption.text}
                    onClick={this._onDropClick}
                >
                    {Icon({ iconName: "ChevronDown", className: "pivotBar-choice-group-icon" })}
                </Button>
                {showDropdown ? (
                    <ContextualMenu
                        shouldFocusOnMount={true}
                        isBeakVisible={false}
                        target={dropTarget}
                        onDismiss={this._onDismiss}
                        directionalHint={DirectionalHint.bottomRightEdge}
                        items={options.map(o => {
                            return {
                                key: o.key,
                                name: o.text,
                                ariaLabel: o.ariaLabel,
                                isChecked: o === selectedOption,
                                canCheck: true,
                                onClick: this._onOptionClick
                            };
                        })}
                    />
                ) : null}
            </div>
        );
    }

    private _getSelectedOptionIndex(options: IChoiceGroupViewActionOption[], defaultSelectedKey: string = null): number {
        const selectedOptionIndex = defaultSelectedKey ? findIndex(options, o => o.key === defaultSelectedKey) : -1;
        return Math.max(selectedOptionIndex, 0);
    }

    private _getNextOptionIndex(options: IChoiceGroupViewActionOption[]): number {
        let nextOptionIndex = this.state.selectedOptionIndex + 1;
        if (nextOptionIndex === options.length) {
            nextOptionIndex = 0;
        }

        return nextOptionIndex;
    }

    private _changeOption(ev: React.MouseEvent<HTMLElement>, index: number): void {
        const { onChange, options } = this.props;
        this.setState({ selectedOptionIndex: index });

        if (onChange) {
            onChange(ev, options[index]);
        }
    }

    private _onCycleClick = (ev: React.MouseEvent<HTMLButtonElement>): void => {
        this._changeOption(ev, this._getNextOptionIndex(this.props.options));
    };

    private _onOptionClick = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem): void => {
        ev.stopPropagation();
        this._changeOption(
            ev,
            findIndex(this.props.options, o => o.key === item.key)
        );
    };

    private _onDropClick = (ev: React.MouseEvent<HTMLButtonElement>): void => {
        this.setState({
            showDropdown: true,
            dropTarget: ev.currentTarget
        });
    };

    private _onDismiss = (event: any) => {
        this.setState({ showDropdown: false });
    };
}

/**
 * ChoiceGroup visualization for bar area.
 */
class ChoiceGroupBarVisualization extends ChoiceGroupVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);
        const selectedOption = this.getSelectedOption(action.key, actionProps, viewOptions);

        // Add item for the selected option which is always visible
        return [
            {
                key: action.key,
                className: css("vss-PivotBar--commandBar-barChoiceGroup", actionProps.className),
                onRender: (item: IContextualMenuItem, dismissMenu: (ev?: any, dismissAll?: boolean) => void, posInSet?: number, setSize?: number) => {
                    return (
                        <ChoiceGroupButton
                            ariaLabel={item.ariaLabel}
                            aria-setsize={setSize}
                            aria-posinset={posInSet}
                            dropButtonAriaLabel={actionProps.dropButtonAriaLabel}
                            defaultSelectedKey={selectedOption.key}
                            options={actionProps.options}
                            dropButtonClassName="vss-PivotBar--commandBar-barChoiceDrop"
                            onChange={(ev: React.MouseEvent<HTMLElement>, option: IChoiceGroupViewActionOption) => {
                                setViewOption(viewOptions, action.key, option.key, ev, action);
                            }}
                        />
                    );
                }
            }
        ];
    }
}

VisualizationRegistration.register(PivotBarActionArea.Bar, PivotBarViewActionType.ChoiceGroup, ChoiceGroupBarVisualization);

/**
 * ChoiceGroup visualization for overflow area.
 */
class ChoiceGroupOverflowVisualization extends ChoiceGroupVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);
        const selectedOption = this.getSelectedOption(action.key, actionProps, viewOptions);

        let items: IContextualMenuItem[] = [];

        // Add header for the choice group inside a popup
        if (action.name) {
            items.push({
                key: `${action.key}-header`,
                name: action.name,
                itemType: ContextualMenuItemType.Header
            });
        }

        // Add each option individually
        for (let option of actionProps.options) {
            items.push({
                key: option.key,
                name: option.text,
                ariaLabel: option.ariaLabel,
                isChecked: option === selectedOption,
                canCheck: true,
                title: option.title,
                onClick: (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
                    setViewOption(viewOptions, action.key, item.key, ev, action);
                    if (viewOptions && ev) {
                        ev.preventDefault();
                    }
                }
            });
        }

        return items;
    }
}

VisualizationRegistration.register(PivotBarActionArea.Overflow, PivotBarViewActionType.ChoiceGroup, ChoiceGroupOverflowVisualization);

/**
 * Text visualization for both bar and overflow areas.
 */
class TextVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    protected getActionProps(action: IPivotBarViewAction): ITextViewActionProps {
        const actionProps = action.actionProps as ITextViewActionProps;
        if (!actionProps || !actionProps.text) {
            throw new Error(`Text must be specified through actionProps for ${action.key}`);
        }

        return actionProps;
    }

    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);

        return [
            {
                key: action.key,
                className: "vss-PivotBar--commandBar-text",
                onRender: (item: IContextualMenuItem) => {
                    const dynamicText = <DynamicText className={actionProps.className} key={`${action.key}-text`} text={actionProps.text} />;
                    if (action.title) {
                        return (
                            <Tooltip text={action.title} key={`${action.key}-tooltip`}>
                                <span>{dynamicText}</span>
                            </Tooltip>
                        );
                    }
                    return dynamicText;
                }
            }
        ];
    }
}

VisualizationRegistration.register(PivotBarActionArea.Bar, PivotBarViewActionType.Text, TextVisualization);
VisualizationRegistration.register(PivotBarActionArea.Overflow, PivotBarViewActionType.Text, TextVisualization);

interface IPivotBarSliderProps extends ISliderProps {
    title: string;
}

class PivotBarSlider extends BaseComponent<IPivotBarSliderProps> {
    private _outerFocus: HTMLElement;
    private _innerFocus: IFocusZone;

    public render(): JSX.Element {
        return (
            <Tooltip text={this.props.title}>
                <div ref={el => (this._outerFocus = el)} onKeyDown={this._onOuterKeyDown} data-is-focusable={true} className="vss-PivotBarSlider">
                    <FocusZone
                        componentRef={fz => (this._innerFocus = fz)}
                        onKeyDown={this._onInnerKeyDown}
                        direction={FocusZoneDirection.horizontal}
                    >
                        <Slider {...this.props} />
                    </FocusZone>
                </div>
            </Tooltip>
        );
    }

    private _onOuterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.which === KeyCode.enter) {
            if (this._innerFocus) {
                this._innerFocus.focus();
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };

    private _onInnerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.which === KeyCode.escape) {
            if (this._outerFocus) {
                this._outerFocus.focus();
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };
}

/**
 * Base visualization for Slider view action.
 */
abstract class SliderVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    protected getActionProps(action: IPivotBarViewAction): ISliderViewActionProps {
        if (!action.actionProps) {
            action.actionProps = {} as ISliderViewActionProps;
        }

        return action.actionProps as ISliderViewActionProps;
    }

    protected getValue(key: string, viewOptions: IViewOptions): number {
        return viewOptions ? viewOptions.getViewOption(key) || 0 : 0;
    }

    protected get useLabel(): boolean {
        return true;
    }

    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const actionProps = this.getActionProps(action);
        const value = this.getValue(action.key, viewOptions);

        // Add item for the selected option which is always visible
        return [
            {
                key: action.key,
                className: "vss-PivotBar--commandBar-barSlider",
                onRender: (item: IContextualMenuItem, dismissMenu: (ev?: any, dismissAll?: boolean) => void, posInSet?: number, setSize?: number) => {
                    return (
                        <PivotBarSlider
                            key={`${action.key}-slider`}
                            className={css("vss-PivotBar--commandBar-slider", actionProps.className)}
                            label={this.useLabel ? action.name : undefined}
                            value={value}
                            min={actionProps.minValue}
                            max={actionProps.maxValue}
                            step={actionProps.step}
                            ariaLabel={action.ariaLabel}
                            aria-setsize={setSize}
                            aria-posinset={posInSet}
                            title={action.title}
                            showValue={false}
                            disabled={action.disabled}
                            onChange={(value: number) => {
                                setViewOption(viewOptions, action.key, value, null, action);
                            }}
                        />
                    );
                }
            }
        ];
    }
}

/**
 * Slider visualization for bar area.
 */
class SliderBarVisualization extends SliderVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {}

VisualizationRegistration.register(PivotBarActionArea.Bar, PivotBarViewActionType.Slider, SliderBarVisualization);

/**
 * Slider visualization for overflow area.
 */
class SliderOverflowVisualization extends SliderVisualization implements IPivotBarActionVisualization<IPivotBarViewAction> {
    protected get useLabel(): boolean {
        return false;
    }

    public getVisualization(action: IPivotBarViewAction, viewOptions: IViewOptions): IContextualMenuItem[] {
        const items = super.getVisualization(action, viewOptions);

        if (action.name) {
            // For overflow area, add label as a header
            items.unshift({
                key: `${action.key}-header`,
                name: action.name,
                itemType: ContextualMenuItemType.Header
            });
        }

        return items;
    }
}

VisualizationRegistration.register(PivotBarActionArea.Overflow, PivotBarViewActionType.Slider, SliderOverflowVisualization);
